<template>
  <v-container fluid class="pa-2">

    <v-layout row wrap align-center class="mb-2">
      <v-flex xs12 sm4 md3>
        <v-menu
          ref="menuFrom"
          v-model="menuFrom"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="dateFrom"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="dateFromFormatted"
            label="Start Date"
            prepend-icon="mdi-calendar"
            readonly
          />
          <v-date-picker
            v-model="dateFrom"
            no-title
            min="2020-01-01"
            @input="$refs.menuFrom.save(dateFrom)"
          />
        </v-menu>
      </v-flex>

      <v-flex xs12 sm4 md3>
        <v-menu
          ref="menuTo"
          v-model="menuTo"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="dateTo"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="dateToFormatted"
            label="End Date"
            prepend-icon="mdi-calendar"
            readonly
          />
          <v-date-picker
            v-model="dateTo"
            no-title
            min="2020-01-01"
            @input="$refs.menuTo.save(dateTo)"
          />
        </v-menu>
      </v-flex>

      <v-flex xs12 sm4 md6 class="text-xs-right">
        <v-progress-circular v-if="itemListLoading" indeterminate color="primary" class="mr-3" />
        <v-btn flat :loading="exporting" @click="downloadExport">
          <v-icon left color="dark_grey">mdi-cloud-download</v-icon> Download Calendar/Item List
        </v-btn>
      </v-flex>
    </v-layout>

    <v-data-table
      :headers="itemListHeaders"
      :items="groupedItemList"
      :loading="itemListLoading"
      class="item-list-table elevation-4"
      hide-actions
    >
      <template slot="headers" slot-scope="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.value"
            class="col-header"
            @click="handleSort(header.value)"
          >
            {{ header.text }}
            <v-icon v-if="sortKey === header.value" small class="sort-icon">
              {{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
        </tr>
      </template>

      <template slot="items" slot-scope="{ item }">

        <!-- Group divider row -->
        <tr v-if="item._isDivider" style="background-color: #e8e8e8 !important;">
          <td
            :colspan="itemListHeaders.length"
            style="padding: 6px 16px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #444; line-height: 1;"
          >
            {{ item.label }}
          </td>
        </tr>

        <!-- Data row -->
        <tr v-else :class="{ 'row-highlighted': item.is_highlighted }">

          <!-- Company (first cell carries the color stripe) -->
          <td
            class="td-stripe"
            :style="stripeStyle(item)"
          >
            <span>{{ item.company }}{{ woDesignators[(item.work_orders || [])[0]] || '' }}</span>
            <v-chip
              v-if="item.is_new"
              small
              color="green"
              text-color="white"
              class="ml-2 chip-new"
            >NEW</v-chip>
          </td>

          <!-- Description (read-only) -->
          <td>{{ item.name }}</td>

          <!-- NorCal Safeway CIC (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'safeway_cic')"
              @cancel="cancelEdit(item, 'safeway_cic')"
              @close="closeEdit(item, 'safeway_cic')"
              @save="saveItem(item, 'safeway_cic')"
            >
              <span :class="{ 'cell-empty': !item.safeway_cic }">
                {{ item.safeway_cic || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'safeway_cic')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.safeway_cic"
                label="Safeway CIC"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>
        
          <!-- UPC (editable; saved to the shared product record) -->
          <td class="text-no-wrap">
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'upc')"
              @cancel="cancelEdit(item, 'upc')"
              @close="closeEdit(item, 'upc')"
              @save="saveItem(item, 'upc')"
            >
              <span :class="{ 'cell-empty': !item.upc }">
                {{ item.upc || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'upc')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.upc"
                label="UPC"
                hint="6 to 14 digits"
                maxlength="14"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- Demo Conducted By (read-only) -->
          <td>{{ item.agency || 'HSP Brands' }}</td>

          <!-- Invoice Cost (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'invoice_cost')"
              @cancel="cancelEdit(item, 'invoice_cost')"
              @close="closeEdit(item, 'invoice_cost')"
              @save="saveItem(item, 'invoice_cost')"
            >
              <span :class="{ 'cell-empty': !item.invoice_cost }">
                {{ item.invoice_cost || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'invoice_cost')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.invoice_cost"
                label="Invoice Cost"
                type="number"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- Contact (popover) -->
          <td class="td-contact">
            <template v-if="contactsFor(item).length">
              <v-menu
                offset-y
                :close-on-content-click="false"
                open-on-click
                max-width="280"
              >
                <span slot="activator" class="contact-trigger">
                  {{ contactsFor(item)[0].name }}
                  <v-icon class="contact-more-icon">mdi-chevron-down</v-icon>
                </span>
                <v-card class="pa-3">
                  <div
                    v-for="(contact, i) in contactsFor(item)"
                    :key="i"
                    :class="{ 'mt-3': i > 0 }"
                  >
                    <div class="contact-popup-name">{{ contact.name }}</div>
                    <div v-if="contact.phone" class="contact-popup-detail">{{ contact.phone }}</div>
                    <div v-if="contact.email" class="contact-popup-detail">{{ contact.email }}</div>
                  </div>
                </v-card>
              </v-menu>
            </template>
            <span v-else class="grey--text">—</span>
          </td>

          <!-- CIC (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'absco_cic')"
              @cancel="cancelEdit(item, 'absco_cic')"
              @close="closeEdit(item, 'absco_cic')"
              @save="saveItem(item, 'absco_cic')"
            >
              <span :class="{ 'cell-empty': !item.absco_cic }">
                {{ item.absco_cic || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'absco_cic')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.absco_cic"
                label="ABSCO CIC"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- Size (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'size')"
              @cancel="cancelEdit(item, 'size')"
              @close="closeEdit(item, 'size')"
              @save="saveItem(item, 'size')"
            >
              <span :class="{ 'cell-empty': !item.size }">
                {{ item.size || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'size')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.size"
                label="Size"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- CIC Validation (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'cic_validation')"
              @cancel="cancelEdit(item, 'cic_validation')"
              @close="closeEdit(item, 'cic_validation')"
              @save="saveItem(item, 'cic_validation')"
            >
              <span :class="{ 'cell-empty': !item.cic_validation }">
                {{ item.cic_validation || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'cic_validation')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.cic_validation"
                label="CIC Validation"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- ABSCO Description (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'absco_description')"
              @cancel="cancelEdit(item, 'absco_description')"
              @close="closeEdit(item, 'absco_description')"
              @save="saveItem(item, 'absco_description')"
            >
              <span :class="{ 'cell-empty': !item.absco_description }">
                {{ item.absco_description || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'absco_description')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-text-field
                slot="input"
                v-model="item.absco_description"
                label="ABSCO Description"
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- Distributor (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'dist')"
              @cancel="cancelEdit(item, 'dist')"
              @close="closeEdit(item, 'dist')"
              @save="saveItem(item, 'dist')"
            >
              <span :class="{ 'cell-empty': !item.dist }">
                {{ item.dist || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'dist')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-select
                slot="input"
                v-model="item.dist"
                :items="controlledOptions(['WNCA', 'DDNC'], item.dist)"
                label="Distribution"
                clearable
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

          <!-- Valid (editable) -->
          <td>
            <v-edit-dialog
              large
              persistent
              lazy
              @open="beginEdit(item, 'valid')"
              @cancel="cancelEdit(item, 'valid')"
              @close="closeEdit(item, 'valid')"
              @save="saveItem(item, 'valid')"
            >
              <span :class="{ 'cell-empty': !item.valid }">
                {{ item.valid || 'Click to add' }}
              </span>
              <v-progress-circular
                v-if="isSaving(item, 'valid')"
                :size="14"
                :width="2"
                indeterminate
                color="primary"
                class="ml-1"
              />
              <v-select
                slot="input"
                v-model="item.valid"
                :items="controlledOptions(['V', 'D'], item.valid)"
                label="Valid"
                clearable
                single-line
                autofocus
              />
            </v-edit-dialog>
          </td>

        </tr>
      </template>

      <template slot="no-data">
        <div class="text-xs-center pa-4 grey--text">
          No items scheduled for this month.
        </div>
      </template>
    </v-data-table>

    <v-snackbar
      v-model="saveNotice.visible"
      :timeout="5000"
      :color="saveNotice.ok ? 'success' : 'error'"
      bottom
    >
      {{ saveNotice.message }}
      <v-btn
        flat
        dark
        @click="saveNotice.visible = false"
      >Close</v-btn>
    </v-snackbar>

  </v-container>
</template>

<script>
import axios from 'axios'
import { TokenService } from '../services/storage.service'
import { downloadCombinedExport } from '../utils/combinedExport'

export default {
  data: () => ({
    dateFrom: localStorage.getItem('retailDateFrom') ||
      new Date().toISOString().substr(0, 7) + '-01',
    dateTo: localStorage.getItem('retailDateTo') ||
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString('sv'),
    menuFrom: false,
    menuTo: false,
    itemList: [],
    itemListLegend: [],
    itemListLoading: false,
    exporting: false,
    editOriginals: {},
    savingCells: {},
    saveNotice: { visible: false, ok: true, message: '' },
    sortKey: 'company',
    sortAsc: true,
    // HSP Brands is us - not a cp_clients row, so its contacts stay hard-coded
    hspContacts: [
      { name: 'Angela French',  phone: '650.418.2016', email: 'tastings@hspbrands.com' },
    ],
    itemListHeaders: [
      { text: 'Company',           value: 'company',           sortable: false },
      { text: 'Description',           value: 'name',              sortable: false },
      { text: 'NORCAL SFWY CIC',       value: 'safeway_cic',       sortable: false },
      { text: 'UPC',               value: 'upc',               sortable: false },
      { text: 'Demo Conducted By',            value: 'agency',            sortable: false },
      { text: 'Invoice Cost',            value: 'invoice_cost',            sortable: false },
      { text: 'Contact Information',           value: 'contact',           sortable: false },
      { text: 'CIC',         value: 'absco_cic',         sortable: false },
      { text: 'Size',         value: 'size',         sortable: false },
      { text: 'CIC Validation',         value: 'cic_validation',         sortable: false },
      { text: 'Description', value: 'absco_description', sortable: false },
      { text: 'DIST',       value: 'dist',              sortable: false },
      { text: 'Valid',       value: 'valid',              sortable: false },
    ]
  }),

  computed: {
    authenticated () {
      return this.$store.state.accessKey != null && this.$store.state.authenticating == false
    },

    dateFromFormatted () {
      return this.formatDate(this.dateFrom)
    },

    dateToFormatted () {
      return this.formatDate(this.dateTo)
    },

    woDesignators () {
      // For companies with >1 work order, map each work_order → ' -1', ' -2', etc.
      // Keyed by work_order (not color) to avoid false matches when the palette cycles.
      const companyWOs = {}
      for (const entry of this.itemListLegend) {
        if (!companyWOs[entry.company]) companyWOs[entry.company] = []
        companyWOs[entry.company].push(entry.work_order)
      }
      const map = {}
      for (const wos of Object.values(companyWOs)) {
        if (wos.length > 1) {
          wos.forEach((wo, i) => { map[wo] = ` -${i + 1}` })
        }
      }
      return map
    },

    groupedItemList () {
      const items = this.itemList
      const key = this.sortKey
      const asc = this.sortAsc
      const strVal = (item, k) => (item[k] != null ? String(item[k]) : '').toLowerCase()

      if (key === 'company') {
        const sorted = [...items].sort((a, b) => {
          const ca = strVal(a, 'company')
          const cb = strVal(b, 'company')
          const cmp = asc ? ca.localeCompare(cb) : cb.localeCompare(ca)
          if (cmp !== 0) return cmp
          const da = (a.work_orders || [])[0] ? (this.woDesignators[(a.work_orders || [])[0]] || '') : ''
          const db = (b.work_orders || [])[0] ? (this.woDesignators[(b.work_orders || [])[0]] || '') : ''
          return da.localeCompare(db)
        })
        const rows = []
        let lastCompany = null
        for (const item of sorted) {
          const company = item.company || ''
          if (company !== lastCompany) {
            rows.push({ _isDivider: true, label: company })
            lastCompany = company
          }
          rows.push(item)
        }
        return rows
      }

      return [...items].sort((a, b) => {
        const va = strVal(a, key)
        const vb = strVal(b, key)
        return asc ? va.localeCompare(vb) : vb.localeCompare(va)
      })
    },
  },

  watch: {
    authenticated (val) {
      if (val) this.fetchItemList()
    },

    dateFrom (val) {
      localStorage.setItem('retailDateFrom', val)
      this.fetchItemList()
    },

    dateTo (val) {
      localStorage.setItem('retailDateTo', val)
      this.fetchItemList()
    },
  },

  created () {
    if (this.authenticated) this.fetchItemList()
  },

  methods: {
    async downloadExport () {
      // Builds the combined Calendar / Item List / Store List workbook. The
      // helper fetches all three datasets fresh, so this button produces the
      // same file here as it does on the Calendar page.
      this.exporting = true
      try {
        await downloadCombinedExport({
          apiRoot: this.$store.state.apiRoot,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo
        })
      } catch (err) {
        console.error('Failed to build export:', err)
        this.showNotice(false, 'The calendar export could not be created. Please try again.')
      } finally {
        this.exporting = false
      }
    },

    contactsFor (item) {
      if (!item.agency) return this.hspContacts
      return item.contact ? [item.contact] : []
    },

    handleSort (key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortKey = key
        this.sortAsc = true
      }
    },

    stripeStyle (item) {
      const colors = (item.colors && item.colors.length) ? item.colors : ['#e0e0e0']
      const n = colors.length
      const totalPx = 7
      if (n === 1) {
        return { boxShadow: `inset ${totalPx}px 0 0 ${colors[0]}` }
      }
      const stops = []
      colors.forEach((color, i) => {
        stops.push(`${color} ${((i / n) * 100).toFixed(2)}%`)
        stops.push(`${color} ${(((i + 1) / n) * 100).toFixed(2)}%`)
      })
      return {
        backgroundImage: `linear-gradient(to bottom, ${stops.join(', ')})`,
        backgroundSize: `${totalPx}px 100%`,
        backgroundRepeat: 'no-repeat'
      }
    },

    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },

    editKey (item, field) {
      return `${item.cp_wine_id}:${field}`
    },

    beginEdit (item, field) {
      this.$set(this.editOriginals, this.editKey(item, field), item[field])
    },

    cancelEdit (item, field) {
      const key = this.editKey(item, field)
      if (Object.prototype.hasOwnProperty.call(this.editOriginals, key)) {
        this.$set(item, field, this.editOriginals[key])
        this.$delete(this.editOriginals, key)
      }
    },

    closeEdit (item, field) {
      // Vuetify emits close after both save and cancel. A save marks the cell as
      // pending synchronously; every other close is an abandoned edit to restore.
      if (!this.isSaving(item, field)) {
        this.cancelEdit(item, field)
      }
    },

    isSaving (item, field) {
      return Boolean(this.savingCells[this.editKey(item, field)])
    },

    showNotice (ok, message) {
      this.saveNotice = { visible: true, ok, message }
    },

    controlledOptions (supported, currentValue) {
      const current = currentValue == null ? '' : String(currentValue).trim()
      return current && !supported.includes(current)
        ? [current, ...supported]
        : supported
    },

    async fetchItemList () {
      this.itemListLoading = true
      const params = new URLSearchParams({
        access_key: TokenService.getAccessKey(),
        date_start: this.dateFrom,
        date_end: this.dateTo
      })
      try {
        const response = await axios.get(
          `${this.$store.state.apiRoot}retail/${TokenService.getLoginCode()}/item-list?${params.toString()}`
        )
        this.itemList = response.data.items || []
        this.itemListLegend = response.data.legend || []
      } catch (err) {
        console.error('Failed to fetch item list:', err)
        this.itemList = []
      } finally {
        this.itemListLoading = false
      }
    },

    async saveItem (item, field) {
      const key = this.editKey(item, field)
      const originalValue = Object.prototype.hasOwnProperty.call(this.editOriginals, key)
        ? this.editOriginals[key]
        : item[field]
      const value = item[field] == null || item[field] === '' ? null : String(item[field]).trim()
      item[field] = value
      this.$set(this.savingCells, key, true)
      try {
        const endpoint = field === 'upc'
          ? `product/${item.cp_wine_id}/upc`
          : `item-list/${item.cp_wine_id}`
        const params = new URLSearchParams({
          access_key: TokenService.getAccessKey(),
          date_start: this.dateFrom,
          date_end: this.dateTo
        })
        const response = await axios.put(
          `${this.$store.state.apiRoot}retail/${TokenService.getLoginCode()}/${endpoint}?${params.toString()}`,
          { [field]: value },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const saved = response.data && response.data.item
        if (!saved) throw new Error('The server did not return the saved item.')
        if (field === 'upc') {
          // UPC belongs to the shared product, so keep every visible row for
          // that product in sync rather than updating only the edited row.
          this.itemList
            .filter(row => row.cp_wine_id === item.cp_wine_id)
            .forEach(row => this.$set(row, 'upc', saved.upc))
        } else {
          Object.keys(saved).forEach(savedField => this.$set(item, savedField, saved[savedField]))
        }
        if (field !== 'upc' && item.is_new) item.is_new = 0
        this.$delete(this.editOriginals, key)
        this.showNotice(true, field === 'upc' ? 'UPC change saved.' : 'Item List change saved.')
      } catch (err) {
        console.error('Failed to save item:', err)
        this.$set(item, field, originalValue)
        this.$delete(this.editOriginals, key)
        const serverMessage = err.response && err.response.data && err.response.data.message
        this.showNotice(false, serverMessage || 'That Item List change was not saved. The previous value has been restored.')
      } finally {
        this.$delete(this.savingCells, key)
      }
    }
  }
}
</script>

<style scoped>
.item-list-table {
  width: 100%;
}

.row-highlighted {
  background-color: #fff8e1;
}

.chip-new {
  font-size: 10px;
  height: 18px;
}

.cell-empty {
  color: #bbb;
  font-style: italic;
  cursor: pointer;
}

.td-stripe {
  padding-left: 12px !important;
}

/* Make the table its own scroll region in both directions. Sticky
   headers stick to the top of this container as it scrolls vertically,
   while the container scrolls horizontally when the table is too wide */
.item-list-table >>> .v-table__overflow {
  overflow: auto;
  max-height: calc(100vh - 160px);
}

.col-header {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-size: 18px;
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #10069F;
  color: #fff !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
}

.col-header:hover {
  background-color: #2317c4;
}

.sort-icon {
  vertical-align: middle;
  margin-left: 2px;
  color: #fff !important;
}

.td-contact {
  white-space: nowrap;
}

.contact-trigger {
  cursor: pointer;
  border-bottom: 1px dashed #999;
  font-size: 13px;
}

.contact-trigger:hover {
  color: #1565C0;
  border-bottom-color: #1565C0;
}

.contact-more-icon {
  font-size: 18px !important;
  color: #1565C0 !important;
  vertical-align: middle;
  margin-left: 2px;
}

.contact-popup-name {
  font-weight: 600;
  font-size: 13px;
  color: #222;
}

.contact-popup-detail {
  font-size: 12px;
  color: #555;
  line-height: 1.6;
}
</style>
